import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import axios from 'axios';
import { 
  Home, 
  ShoppingCart, 
  Package, 
  CreditCard, 
  FileText, 
  User, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  Check,
  CheckCheck,
  Wallet,
} from 'lucide-react';
import { B2BonixLogo } from '../B2BonixLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DealerSidebarContext = createContext();

const menuItems = [
  { path: '/dealer', icon: Home, label: 'Ana Sayfa', exact: true },
  { path: '/dealer/shop', icon: ShoppingCart, label: 'Sipariş Ver' },
  { path: '/dealer/orders', icon: Package, label: 'Siparişlerim' },
  { path: '/dealer/payments', icon: CreditCard, label: 'Ödemeler' },
  { path: '/dealer/account', icon: FileText, label: 'Cari Hareketler' },
  { path: '/dealer/profile', icon: User, label: 'Profilim' },
];

const DealerSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { sidebarOpen, setSidebarOpen } = useContext(DealerSidebarContext);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
          <Link to="/dealer" className="flex items-center gap-2">
            <B2BonixLogo variant="dark" iconSize={34} asLink={false} showSubtitle={false} />
            <span className="text-xs text-gray-400 border-l border-gray-200 pl-2">Bayi Portalı</span>
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-gray-100 rounded"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#d8dcee] flex items-center justify-center">
              <span className="text-[#1a224f] font-semibold text-sm">
                {user?.name?.charAt(0) || 'B'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 text-sm truncate">{user?.name || 'Bayi'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.company_name || user?.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active 
                    ? 'bg-[#eef0f7] text-[#141a3d]' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-[#1a224f]' : 'text-gray-400'}`} />
                {item.label}
                {active && <ChevronRight className="w-4 h-4 ml-auto text-[#5b67a0]" />}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 text-gray-400" />
            Çıkış Yap
          </button>
        </div>
      </aside>
    </>
  );
};

const DealerTopbar = ({ title }) => {
  const { setSidebarOpen } = useContext(DealerSidebarContext);
  const { user } = useAuthStore();

  return (
    <header className="sticky top-0 z-30 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6">
      {/* Left: Menu button & Title */}
      <div className="flex items-center gap-3">
        <button
          className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Cart Link */}
        <Link 
          to="/dealer/cart"
          className="relative p-2 hover:bg-gray-100 rounded-lg"
          data-testid="cart-btn"
        >
          <ShoppingCart className="w-5 h-5 text-gray-600" />
        </Link>

        {/* Notifications */}
        <NotificationBell />

        {/* User Avatar */}
        <Link to="/dealer/profile" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#d8dcee] flex items-center justify-center">
            <span className="text-[#1a224f] font-semibold text-xs">
              {user?.name?.charAt(0) || 'B'}
            </span>
          </div>
        </Link>
      </div>
    </header>
  );
};

const NotificationBell = () => {
  const { token } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setNotifications(notifRes.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const formatTime = (d) => {
    if (!d) return '';
    const diff = Date.now() - new Date(d).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Az önce';
    if (mins < 60) return `${mins} dk önce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} saat önce`;
    const days = Math.floor(hours / 24);
    return `${days} gün önce`;
  };

  return (
    <div className="relative">
      <button onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }} className="relative p-2 hover:bg-gray-100 rounded-lg" data-testid="notification-bell">
        <Bell className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-[#3a4680] text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">Bildirimler</h3>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#1a224f] hover:text-[#141a3d] flex items-center gap-1">
                  <CheckCheck className="w-3.5 h-3.5" />Tümünü Okundu Yap
                </button>
              )}
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">Bildirim yok</div>
              ) : (
                notifications.slice(0, 20).map(n => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 transition-colors ${!(n.is_read || n.read) ? 'bg-[#eef0f7]/40' : ''}`}
                    onClick={() => { if (!(n.is_read || n.read)) markRead(n.id); }}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!(n.is_read || n.read) && <div className="w-2 h-2 rounded-full bg-[#3a4680] mt-1.5 shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{formatTime(n.created_at)}</p>
                      </div>
                      {!(n.is_read || n.read) && (
                        <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-1 hover:bg-gray-200 rounded shrink-0" title="Okundu yap">
                          <Check className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const DealerBottomNav = () => {
  const location = useLocation();

  const bottomItems = [
    { path: '/dealer', icon: Home, label: 'Ana Sayfa', exact: true },
    { path: '/dealer/shop', icon: ShoppingCart, label: 'Sipariş' },
    { path: '/dealer/orders', icon: Package, label: 'Siparişler' },
    { path: '/dealer/account', icon: Wallet, label: 'Cari' },
  ];

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 lg:hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="flex items-center justify-around h-14">
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full ${
                active ? 'text-[#1a224f]' : 'text-gray-400'
              }`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs mt-1">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export const DealerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Get page title based on route
  const getTitle = () => {
    const titles = {
      '/dealer': 'Ana Sayfa',
      '/dealer/shop': 'Sipariş Ver',
      '/dealer/orders': 'Siparişlerim',
      '/dealer/payments': 'Ödemeler',
      '/dealer/account': 'Cari Hareketler',
      '/dealer/profile': 'Profilim',
      '/dealer/cart': 'Sepetim',
    };
    return titles[location.pathname] || 'Bayi Portalı';
  };

  return (
    <DealerSidebarContext.Provider value={{ sidebarOpen, setSidebarOpen }}>
      <div className="min-h-screen bg-gray-50">
        <DealerSidebar />
        <div className="lg:pl-64">
          <DealerTopbar title={getTitle()} />
          <main className="p-4 pb-20 lg:p-6 lg:pb-6">
            <Outlet />
          </main>
        </div>
        <DealerBottomNav />
      </div>
    </DealerSidebarContext.Provider>
  );
};

export default DealerLayout;
